import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { TimeSelect } from "@/components/ui/time-select";
import { BookingCalendar } from "./BookingCalendar";
import { Users, Calendar, Filter, Clock, X } from "lucide-react";
import { addDays } from "date-fns";
import { Show } from "@/lib/mockData";
import { useTranslation } from "react-i18next";
import { useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface ShowFiltersProps {
  selectedShow?: Show;
  pendingDate: Date | undefined;
  setPendingDate: (date: Date | undefined) => void;
  pendingGroupSize: number;
  setPendingGroupSize: (size: number) => void;
  pendingStartTime?: string;
  setPendingStartTime?: (time: string) => void;
  pendingEndTime?: string;
  setPendingEndTime?: (time: string) => void;
  getDateAvailability: (
    date: Date,
  ) => "available" | "partial" | "unavailable" | undefined;
  isDateAvailable: (date: Date) => boolean;
  isLoadingAvailability?: boolean;
  showApplyButtons?: boolean;
  minParticipants?: number;
  maxParticipants?: number;
  focusSection?: "time" | "date" | "group" | null;
  onDateClick?: (date: Date) => void;
  isMobile?: boolean;
  onClose?: () => void;
}

export function ShowFilters({
  selectedShow,
  pendingDate,
  setPendingDate,
  pendingGroupSize,
  setPendingGroupSize,
  pendingStartTime,
  setPendingStartTime,
  pendingEndTime,
  setPendingEndTime,
  getDateAvailability,
  isDateAvailable,
  isLoadingAvailability,
  showApplyButtons = true,
  minParticipants = 3,
  maxParticipants = 9,
  focusSection,
  onDateClick,
  isMobile = false,
  onClose,
}: ShowFiltersProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || "en";
  const locale = i18n.language;
  const groupRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLDivElement>(null);

  // Scroll to focused section
  useEffect(() => {
    if (focusSection) {
      const refs = {
        group: groupRef,
        date: dateRef,
        time: timeRef,
      };

      const targetRef = refs[focusSection];
      if (targetRef?.current) {
        // Wait for drawer animation to complete
        setTimeout(() => {
          targetRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 600); // Increased delay to account for drawer animation
      }
    }
  }, [focusSection]);

  // Handle date selection with auto-close on mobile
  const handleDateClick = (date: Date | undefined) => {
    setPendingDate(date);
    // If on mobile and onApply is provided, automatically apply and close
    if (isMobile && onDateClick && date) {
      // Small delay to show the selection before closing
      setTimeout(() => {
        onDateClick(date);
      }, 200);
    }
  };
  return (
    <>
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background px-4 pt-4 pb-4 border-b">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            {t("booking.shows.filters.filterOptions")}
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-sm bg-white text-black p-1 shadow-sm ring-offset-background transition-all hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
              aria-label={t("booking.shows.filters.close")}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">
                {t("booking.shows.filters.close")}
              </span>
            </button>
          )}
        </div>
        {selectedShow && (
          <div className="space-y-2 mt-2">
            <h3 className="text-sm font-medium">{selectedShow.name}</h3>
            <img
              src={selectedShow.image}
              alt={selectedShow.name}
              className="w-full h-20 object-cover rounded"
            />
          </div>
        )}
      </div>

      {/* Calendar and Filters */}
      <div className="p-4 space-y-6">
        <div
          ref={groupRef}
          className={cn(
            "space-y-3 transition-all duration-500 rounded-md p-3 -mx-2 relative",
            focusSection === "group" && "bg-gray-100",
          )}
        >
          <Label className="text-sm font-medium flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            {t("booking.shows.filters.participants")}
          </Label>

          <Slider
            min={minParticipants}
            max={maxParticipants + 1}
            step={1}
            value={[pendingGroupSize]}
            onValueChange={([value]) => {
              setPendingGroupSize(value);
            }}
            className="w-full"
          />

          <div className="space-y-1">
            <p className="text-sm font-medium">
              {t("booking.shows.filters.aroundParticipants", {
                count: pendingGroupSize,
              })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("booking.shows.filters.groupSizeDescription", {
                min: Math.max(0, pendingGroupSize - 3),
                max: pendingGroupSize + 3,
              })}
            </p>
          </div>
        </div>

        {/* Time Slot Selection - Moved above calendar for mobile */}
        {isMobile &&
          pendingStartTime !== undefined &&
          pendingEndTime !== undefined &&
          setPendingStartTime &&
          setPendingEndTime && (
            <>
              <div className="h-px bg-border"></div>

              <div
                ref={timeRef}
                className={cn(
                  "space-y-3 transition-all duration-500 rounded-md p-3 -mx-2 relative",
                  focusSection === "time" && "bg-gray-100",
                )}
              >
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {t("booking.shows.filters.timeSlot")}
                </Label>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="start-time"
                      className="text-xs font-medium mb-2 block text-left"
                    >
                      {t("booking.shows.filters.from")}
                    </Label>
                    <TimeSelect
                      value={pendingStartTime}
                      onChange={setPendingStartTime}
                      max={pendingEndTime}
                      interval={30}
                      locale={locale}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="end-time"
                      className="text-xs font-medium mb-2 block text-left"
                    >
                      {t("booking.shows.filters.to")}
                    </Label>
                    <TimeSelect
                      value={pendingEndTime}
                      onChange={setPendingEndTime}
                      min={pendingStartTime}
                      interval={30}
                      locale={locale}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

        <div className="h-px bg-border"></div>

        <div
          ref={dateRef}
          className={cn(
            "space-y-3 transition-all duration-500 rounded-md p-3 -mx-2 relative",
            focusSection === "date" && "bg-gray-100",
          )}
        >
          <Label className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            {t("booking.shows.filters.selectDate")}
          </Label>
          <div className="flex justify-center">
            <BookingCalendar
              mode="single"
              selected={pendingDate}
              onDateClick={handleDateClick}
              className="rounded-md"
              getDateAvailability={getDateAvailability}
              isLoadingAvailability={isLoadingAvailability}
              disabled={{ before: new Date() }}
              startMonth={new Date()}
              defaultMonth={pendingDate || new Date()}
              endMonth={addDays(new Date(), 365)}
              lang={lang}
            />
          </div>
        </div>
      </div>
    </>
  );
}
